/**
 * Free and disposable email providers, rejected when creating client users.
 *
 * A client company's people are no longer restricted to the company's own
 * domains — subsidiaries, consultants and contractors legitimately use their
 * own addresses. The remaining rule is that the address must belong to some
 * organization, so personal and throwaway providers are turned away.
 *
 * Browser-safe on purpose: the admin form validates against this same set, so
 * the message a user sees and the rule the server enforces cannot drift apart.
 * The throwing server-side counterpart is `assertCorporateEmail`.
 *
 * Latin American variants of the large providers are listed explicitly because
 * they are what Delta Protect's clients actually type.
 */
export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'hotmail.com', 'hotmail.es', 'hotmail.com.mx', 'hotmail.com.ar', 'hotmail.com.br',
  'hotmail.co.uk', 'hotmail.fr', 'hotmail.it',
  'outlook.com', 'outlook.es', 'outlook.com.br', 'outlook.cl',
  'live.com', 'live.com.mx', 'live.com.ar', 'live.co.uk',
  'msn.com', 'passport.com',
  // Yahoo
  'yahoo.com', 'yahoo.com.mx', 'yahoo.com.ar', 'yahoo.com.br', 'yahoo.es',
  'yahoo.co.uk', 'yahoo.com.co', 'ymail.com', 'rocketmail.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // Privacy-focused
  'proton.me', 'protonmail.com', 'pm.me', 'tutanota.com', 'tutanota.de', 'tuta.io',
  'hushmail.com',
  // Other global providers
  'aol.com', 'gmx.com', 'gmx.net', 'gmx.de', 'zoho.com', 'zohomail.com',
  'mail.com', 'email.com', 'yandex.com', 'yandex.ru', 'fastmail.com',
  'inbox.com', 'love.com', 'usa.com',
  // Latin American ISPs and portals
  'uol.com.br', 'bol.com.br', 'ig.com.br', 'terra.com.br', 'terra.com.mx',
  'terra.com.co', 'terra.cl', 'terra.es', 'prodigy.net.mx',
  // Disposable / throwaway
  'mailinator.com', 'guerrillamail.com', 'guerrillamail.net', 'sharklasers.com',
  '10minutemail.com', 'temp-mail.org', 'tempmail.com', 'throwawaymail.com',
  'yopmail.com', 'trashmail.com', 'getnada.com', 'maildrop.cc', 'dispostable.com',
  'fakeinbox.com', 'mailnesia.com', 'mytemp.email',
]);

/**
 * The domain part of an email address, lowercased, or null when the value is
 * not shaped like an address. Callers decide what a missing domain means.
 */
export function emailDomain(email: string): string | null {
  const parts = email.trim().toLowerCase().split('@');
  if (parts.length !== 2) return null;
  const domain = parts[1].trim();
  return domain.length > 0 ? domain : null;
}

/** True when the address belongs to a free or disposable provider. */
export function isFreeEmailDomain(email: string): boolean {
  const domain = emailDomain(email);
  return domain !== null && FREE_EMAIL_DOMAINS.has(domain);
}
